import { ErrorBoundary } from '@datadog/rum-react-integration';
import { OktaAuth } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import { ConnectedRouter } from 'connected-react-router';
import moment from 'moment';
import React, { FC, useEffect, useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet';
import { IntlProvider } from 'react-intl';
import { QueryClientProvider } from 'react-query';
import { Provider, useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { ThemeProvider } from 'styled-components';
import { AccountNotFound } from 'components/organisms/account';
import { AppCrash } from 'components/organisms/app-crash';
import { TokenErrorStatus, TokenError } from 'components/organisms/token-error';
import { GlobalStyle } from 'components/styles/global';
import { theme } from 'components/styles/theme';
import { APP_CRASH_KEY, RoutePaths } from 'constant';
import { CustomScroll, OsList } from 'customScroll';
import { setQueryClientAction } from 'data/core/actions';
import { OzoneFonts } from 'ozone/atoms';
import { queryClient, useAccountMetaQuery } from 'queries';
import { MobileViewOverlay } from 'routes/mobile-view-overlay';
import { Routes } from 'routes/routes';
import { setOkta, getOkta, logger } from 'services';
import { localeData } from 'translations/en-moment';
import translations from 'translations/en.json';
import { PageLoading } from './components/atoms/loading';
import { CONFIG } from './config/okta';
import { store, history } from './data';

const VOID_FN = () => {};

const SetupOkta = React.memo(() => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  useEffect(() => {
    dispatch(setQueryClientAction(queryClient));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, queryClient]);
  const { data, isLoading } = useAccountMetaQuery({
    onSuccess: ({ okta: { clientId, issuerDomain } }) => {
      const oktaConfig = CONFIG(issuerDomain, clientId);
      const authInstance = new OktaAuth(oktaConfig);

      setOkta(authInstance);
    },
    onError: (err) => {
      logger.error('Account meta failed', { error: err as Error });
    },
  });

  const oktaAuth = getOkta();
  const isAccountActionPage = pathname === RoutePaths.activate;

  return isLoading ? (
    <PageLoading />
  ) : oktaAuth && data ? (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={VOID_FN}>
      <Helmet
        titleTemplate={`%s - ${data.company?.companyName}`}
        defaultTitle={data.company?.companyName}
      >
        <link rel="icon" type="image/png" href={data.company?.companyLogoUrl} />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href={data.company?.companyLogoUrl}
        />
      </Helmet>
      <Routes />
    </Security>
  ) : isAccountActionPage ? (
    <TokenError status={TokenErrorStatus.AlreadyCompleted} />
  ) : (
    <AccountNotFound />
  );
});

export const App: FC = () => {
  const locale = 'en';

  useLayoutEffect(() => {
    moment.updateLocale(locale, localeData);
  }, [locale]);

  const fallback = (error: Error) => {
    logger.error(`${APP_CRASH_KEY}: ${error.message}`, {
      error,
    });

    return (
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <AppCrash />
        </Provider>
      </QueryClientProvider>
    );
  };

  return (
    <ErrorBoundary fallback={fallback}>
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <ConnectedRouter history={history}>
            <ThemeProvider theme={theme}>
              <IntlProvider locale={locale} messages={translations}>
                <MobileViewOverlay>
                  <CustomScroll blacklist={[OsList.Mac]} />
                  <OzoneFonts />
                  <SetupOkta />
                </MobileViewOverlay>
              </IntlProvider>
              <GlobalStyle />
            </ThemeProvider>
          </ConnectedRouter>
        </Provider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};
